/**
 * Footer — Always Dark, Always Below Fold
 *
 * Visible only when the user scrolls below the viewport-filling main area.
 * All texts and URLs come from market config — no hardcoded strings.
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#footer
 */
import { useAppStore } from '../../store/app.store';
import type { ResolvedConfig } from '@ims/shared';

/** Language label map — short uppercase labels for the switcher. */
const LANG_LABELS: Record<string, string> = {
  de: 'DE',
  en: 'EN',
  es: 'ES',
  cs: 'CZ',
  pl: 'PL',
  sv: 'SE',
  fr: 'FR',
};

interface FooterProps {
  config?: ResolvedConfig | null;
}

export function Footer({ config }: FooterProps) {
  const reopenConsent = useAppStore((s) => s.reopenConsent);
  const screen = useAppStore((s) => s.screen);
  const consumptionDisclaimer = useAppStore((s) => s.consumptionDisclaimer);

  const ui = config?.ui ?? {};
  const legal = config?.market.legal ?? {};
  const disclaimer = config?.appDisclaimer ?? '';
  const year = new Date().getFullYear();
  const copyright = (ui.copyright ?? '© {year} AUDI AG. All rights reserved.').replace(
    '{year}',
    String(year),
  );

  // ── Language switcher ──────────────────────────────────────────────
  const supportedLangs = config?.market.supportedLanguages ?? [];
  const currentLang = new URLSearchParams(window.location.search).get('lang');
  // The market's base language is the first segment of the locale (e.g. "de-DE" → "de").
  const baseLang = config?.market.locale?.split('-')[0]?.toLowerCase();

  function switchLang(lang: string) {
    const url = new URL(window.location.href);
    if (lang === baseLang) {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', lang);
    }
    // eslint-disable-next-line react-hooks/immutability -- intentional: full page navigation to switch language
    window.location.href = url.toString();
  }

  // Build link list — only include entries where a URL is configured
  const links: { label: string; href: string }[] = [
    { label: ui.imprintLabel ?? 'Imprint', href: legal.imprintUrl ?? '' },
    { label: ui.privacyLabel ?? 'Privacy policy', href: legal.privacyUrl ?? '' },
    { label: ui.cookiePolicyLabel ?? 'Cookie policy', href: legal.cookiePolicyUrl ?? '' },
    { label: ui.termsLabel ?? 'Terms of use', href: legal.termsUrl ?? '' },
    { label: ui.licensesLabel ?? 'Licences', href: legal.licensesUrl ?? '' },
  ].filter((l) => l.href !== '');

  const cookieSettingsLabel = ui.cookieSettingsLabel ?? 'Cookie settings';

  return (
    <footer className="shrink-0 bg-brand-primary text-brand-text-secondary px-5 py-8 flex flex-col gap-5">
      {disclaimer &&
        disclaimer.split('\n\n').map((para, i) => (
          <p key={i} className="text-xs leading-relaxed">
            {para}
          </p>
        ))}

      {screen === 'viewer' && consumptionDisclaimer && (
        <p className="text-xs leading-relaxed">{consumptionDisclaimer}</p>
      )}

      <hr className="border-brand-border/30" />

      <nav
        className="grid grid-cols-2 gap-x-4 gap-y-5 sm:flex sm:flex-wrap sm:gap-x-8 sm:gap-y-3"
        aria-label="Footer navigation"
      >
        {links.map(({ label, href }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm hover:text-brand-text transition-colors"
          >
            {label}
          </a>
        ))}
        <button
          onClick={() => reopenConsent()}
          className="text-sm hover:text-brand-text transition-colors text-left"
        >
          {cookieSettingsLabel}
        </button>
      </nav>

      <div className="flex items-center justify-between">
        <p className="text-xs">{copyright}</p>
        {supportedLangs.length > 1 && (
          <div className="flex items-center gap-1 text-xs" role="group" aria-label="Language">
            {supportedLangs.map((lang, i) => {
              const isActive = lang === baseLang ? !currentLang : currentLang === lang;
              return (
                <span key={lang} className="flex items-center">
                  {i > 0 && <span className="text-brand-border/50 mx-1">|</span>}
                  <button
                    onClick={() => switchLang(lang)}
                    className={`transition-colors ${isActive ? 'text-brand-text font-semibold' : 'text-brand-text-secondary hover:text-brand-text'}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {LANG_LABELS[lang] ?? lang.toUpperCase()}
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </footer>
  );
}
